import { useCallback, useMemo } from 'react'
import { t } from '@lingui/macro'
import { useSnackbar } from 'notistack'
import { useGetExpertiseQuery } from '@/core/services/expertise'
import { useAddFileMutation, useDeleteFilesMutation } from '@/core/services/file'
import { DocumentsProps } from '@/pages/expertise/components/Documents'
import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { getApiErrorMessage } from '@/utils/errors'

export function useExpertiseDocuments(expertiseId: string) {
  const { enqueueSnackbar } = useSnackbar()

  const authUser = useAppSelector(selectAuthUser)

  const { data: expertise } = useGetExpertiseQuery(expertiseId)
  const [addFile] = useAddFileMutation()
  const [deleteFiles, { isLoading: isDeletingFiles }] = useDeleteFilesMutation()

  const documents = useMemo(() => {
    return (
      expertise?.files.map((file) => ({
        filename: file.filename,
        key: file.key,
        url: file.url,
        canDelete: authUser?._id === expertise?.createdBy._id ? true : authUser?._id === file.uploadedBy,
      })) ?? []
    )
  }, [authUser, expertise])

  const deleteDocuments = useCallback(
    async (deletedDocuments: DocumentsProps['documents']) => {
      if (!expertise || !deletedDocuments.length) return
      const files = deletedDocuments.map((document) => ({
        filename: document.filename,
        key: document.key,
        objectType: 'expertise' as const,
      }))
      try {
        await deleteFiles({ files, objectId: expertise._id, objectType: 'expertise' }).unwrap()
        enqueueSnackbar(t`Documents have been deleted successfully.`, { variant: 'success' })
      } catch (error) {
        enqueueSnackbar(getApiErrorMessage(error, t`An error has occured while deleting the document. Please try again!`), { variant: 'error' })
      }
    },
    [expertise]
  )

  const uploadDocument = useCallback(
    async (file: File) => {
      if (!expertise) return
      try {
        await addFile({ file, objectType: 'expertise', objectId: expertise._id }).unwrap()
        enqueueSnackbar(t`Documents have been sent successfully.`, { variant: 'success' })
      } catch (error) {
        enqueueSnackbar(getApiErrorMessage(error, t`An error has occured while saving the document. Please try again!`), { variant: 'error' })
      }
    },
    [expertise]
  )

  const canUpload = authUser?._id === expertise?.createdBy._id

  return {
    canUpload,
    documents,
    deleteDocuments,
    isDeletingFiles,
    uploadDocument,
  }
}
